import { useState } from "react";
import Layout from "../components/Layout";
import PageHeader from "../components/PageHeader";
import CallToAction from "../components/CallToAction";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check } from "lucide-react";

// Define price categories and items
const categories = [
  {
    id: "face",
    name: "Obraz",
  },
  {
    id: "body",
    name: "Telo",
  },
  {
    id: "hands",
    name: "Roke",
  },
  {
    id: "non-surgical",
    name: "Nekirurški posegi",
  }
];

const priceItems = [
  // Face procedures
  {
    id: 1,
    name: "Korekcija vek (zgornje)",
    price: 1200,
    duration: "1-2 uri",
    recovery: "1-2 tedna",
    category: "face",
    popular: true,
  },
  {
    id: 2,
    name: "Korekcija vek (spodnje)",
    price: 1300,
    duration: "1-2 uri",
    recovery: "1-2 tedna",
    category: "face",
  },
  {
    id: 3,
    name: "Korekcija vek (zgornje in spodnje)",
    price: 2200,
    duration: "2-3 ure",
    recovery: "1-2 tedna",
    category: "face",
    popular: true,
  },
  {
    id: 4,
    name: "Korekcija nosu",
    price: 3500,
    duration: "2-3 ure",
    recovery: "2-3 tedne",
    category: "face",
  },
  {
    id: 5,
    name: "Lifting obraza",
    price: 4800,
    duration: "3-4 ure",
    recovery: "2-3 tedne",
    category: "face",
    popular: true,
  },
  // Body procedures
  {
    id: 6,
    name: "Povečanje prsi",
    price: 4500,
    duration: "1-2 uri",
    recovery: "1-2 tedna",
    category: "body",
    popular: true,
  },
  {
    id: 7,
    name: "Dvig prsi",
    price: 4200,
    duration: "2-3 ure",
    recovery: "2-3 tedne",
    category: "body",
  },
  {
    id: 8,
    name: "Liposukcija (ena regija)",
    price: 2500,
    duration: "1-2 uri",
    recovery: "1-2 tedna",
    category: "body",
  },
  {
    id: 9,
    name: "Abdominoplastika",
    price: 4800,
    duration: "2-3 ure",
    recovery: "3-4 tedne",
    category: "body",
    popular: true,
  },
  // Hand procedures
  {
    id: 10,
    name: "Rekonstrukcija roke",
    price: 2800,
    duration: "2-4 ure",
    recovery: "2-4 tedne",
    category: "hands",
  },
  {
    id: 11,
    name: "Operacija karpalnega kanala",
    price: 1400,
    duration: "1 ura",
    recovery: "1-2 tedna",
    category: "hands",
  },
  {
    id: 12,
    name: "Korekcija prstov",
    price: 1800,
    duration: "1-2 uri",
    recovery: "2-3 tedne",
    category: "hands",
  },
  // Non-surgical procedures
  {
    id: 13,
    name: "Botoks (ena regija)",
    price: 250,
    duration: "30 min",
    recovery: "Brez",
    category: "non-surgical",
    popular: true,
  },
  {
    id: 14,
    name: "Hialuronski filerji (1ml)",
    price: 380,
    duration: "30 min",
    recovery: "Brez",
    category: "non-surgical",
    popular: true,
  },
  {
    id: 15,
    name: "Kemični piling",
    price: 150,
    duration: "30 min",
    recovery: "3-7 dni",
    category: "non-surgical",
  },
];

const PricingPage = () => {
  const [activeCategory, setActiveCategory] = useState("all");

  const filteredPrices = activeCategory === "all" 
    ? priceItems 
    : priceItems.filter(item => item.category === activeCategory);

  return (
    <Layout>
      <PageHeader
        title="Cenik storitev"
        description="Transparenten pregled cen vseh naših storitev."
      />
      
      {/* Categories Filter */}
      <section className="bg-white py-12">
        <div className="container mx-auto px-6 md:px-8">
          <div className="max-w-3xl mx-auto text-center mb-8">
            <p className="text-gray-700">
              Spodaj je predstavljen okvirni cenik naših storitev. Za natančnejšo oceno in individualno 
              prilagojene posege vas vabimo na osebni posvet. Vse cene so v EUR in vključujejo DDV.
            </p>
          </div>
          
          <div className="flex flex-wrap gap-3 justify-center mb-8">
            <Button 
              variant={activeCategory === "all" ? "default" : "outline"} 
              className={`rounded-full ${activeCategory === "all" ? 'bg-medical-500' : 'border-medical-300'}`}
              onClick={() => setActiveCategory("all")}
            >
              Vse storitve
            </Button>
            
            {categories.map(category => (
              <Button 
                key={category.id}
                variant={activeCategory === category.id ? "default" : "outline"}
                className={`rounded-full ${activeCategory === category.id ? 'bg-medical-500' : 'border-medical-300'}`}
                onClick={() => setActiveCategory(category.id)}
              >
                {category.name}
              </Button>
            ))}
          </div>
        </div>
      </section>
      
      {/* Pricing Table */}
      <section className="bg-gray-50 py-12 md:py-16">
        <div className="container mx-auto px-6 md:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="bg-white rounded-3xl shadow-sm overflow-hidden">
              <div className="grid grid-cols-12 bg-medical-50 py-4 px-6 text-gray-700 font-medium">
                <div className="col-span-5 md:col-span-6">Storitev</div>
                <div className="col-span-3 md:col-span-2 text-center">Trajanje</div>
                <div className="col-span-4 text-right">Cena (€)</div>
              </div>
              
              <div className="divide-y divide-gray-100">
                {filteredPrices.map((item) => (
                  <div 
                    key={item.id}
                    className={`grid grid-cols-12 py-5 px-6 items-center ${
                      item.popular ? 'bg-medical-50/30' : ''
                    }`}
                  >
                    <div className="col-span-5 md:col-span-6 flex items-center">
                      <div>
                        <span className="text-gray-900 font-medium">{item.name}</span>
                        {item.popular && (
                          <Badge className="ml-2 bg-medical-500 text-white">Priljubljeno</Badge>
                        )}
                        <p className="text-sm text-gray-500 mt-1">Okrevanje: {item.recovery}</p>
                      </div>
                    </div>
                    <div className="col-span-3 md:col-span-2 text-center text-gray-600">{item.duration}</div>
                    <div className="col-span-4 text-right text-medical-600 font-semibold">{item.price}</div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="mt-12 glass-card p-6 md:p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Pomembne informacije</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start">
                  <div className="bg-medical-100 p-1.5 rounded-full text-medical-600 mr-3 mt-0.5">
                    <Check size={16} />
                  </div>
                  <p className="text-gray-700">Cene vključujejo celotno obravnavo (predoperativni pregled, operacijo in pooperativne preglede).</p>
                </div>
                <div className="flex items-start">
                  <div className="bg-medical-100 p-1.5 rounded-full text-medical-600 mr-3 mt-0.5">
                    <Check size={16} />
                  </div>
                  <p className="text-gray-700">Za natančno ceno posega je potreben osebni pregled in posvet.</p>
                </div>
                <div className="flex items-start">
                  <div className="bg-medical-100 p-1.5 rounded-full text-medical-600 mr-3 mt-0.5">
                    <Check size={16} />
                  </div>
                  <p className="text-gray-700">Možnost plačila na obroke brez obresti (do 24 obrokov).</p>
                </div>
                <div className="flex items-start">
                  <div className="bg-medical-100 p-1.5 rounded-full text-medical-600 mr-3 mt-0.5">
                    <Check size={16} />
                  </div>
                  <p className="text-gray-700">Prvi posvetovalni pregled stane 50€ in se odšteje od cene posega.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <CallToAction 
        title="Želite individualno ponudbo?"
        subtitle="Kontaktirajte nas za brezplačen informativni posvet in natančno oceno stroškov."
      />
    </Layout>
  );
};

export default PricingPage;